import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import api, { fmtErr } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { setToken } from "../../lib/authStore";
import { KeyRound } from "lucide-react";

export default function ChangePasswordForm({ onDone }) {
  const { t } = useTranslation();
  const { refresh } = useAuth();
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) return toast.error(t("security.tooShort"));
    if (next !== confirm) return toast.error(t("security.mismatch"));
    setBusy(true);
    try {
      const { data } = await api.post("/auth/change-password", { current_password: cur, new_password: next });
      if (data.token) setToken(data.token);
      await refresh();
      toast.success(t("security.changed"));
      setCur(""); setNext(""); setConfirm("");
      onDone && onDone();
    } catch (e) { toast.error(fmtErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="change-password-form">
      <div>
        <label className="block text-xs font-mono-accent text-zinc-500 mb-1 tracking-wider">{t("security.current")}</label>
        <input data-testid="cp-current" type="password" autoComplete="current-password" value={cur}
          onChange={e=>setCur(e.target.value)} className="cp-input" required />
      </div>
      <div>
        <label className="block text-xs font-mono-accent text-zinc-500 mb-1 tracking-wider">{t("security.new")}</label>
        <input data-testid="cp-new" type="password" autoComplete="new-password" value={next}
          onChange={e=>setNext(e.target.value)} className="cp-input" required minLength={8} />
      </div>
      <div>
        <label className="block text-xs font-mono-accent text-zinc-500 mb-1 tracking-wider">{t("security.confirm")}</label>
        <input data-testid="cp-confirm" type="password" autoComplete="new-password" value={confirm}
          onChange={e=>setConfirm(e.target.value)} className="cp-input" required minLength={8} />
      </div>
      <button data-testid="cp-submit" disabled={busy} className="cp-btn">
        {busy ? t("common.loading") : t("security.change")}
      </button>
      <style>{`
        .cp-input { width:100%; background:rgba(0,0,0,0.5); border:1px solid rgba(255,255,255,0.1); border-radius:12px; padding:12px 14px; color:white; }
        .cp-input:focus { outline:none; border-color:#E60000; }
        .cp-btn { width:100%; background:#E60000; color:white; font-weight:600; padding:12px; border-radius:9999px; transition:background-color .2s; }
        .cp-btn:hover { background:#FF1A1A; }
        .cp-btn:disabled { opacity:0.5; }
      `}</style>
    </form>
  );
}

export function ForcePasswordGate() {
  const { user } = useAuth();
  const { t } = useTranslation();
  if (!user || !user.must_change_password) return null;
  return (
    <div className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-xl flex items-center justify-center p-5"
      data-testid="force-password-gate">
      <div className="w-full max-w-md glass rounded-3xl border border-[#E60000]/30 p-6 shadow-[0_0_40px_rgba(230,0,0,0.25)]">
        <div className="flex items-center gap-2 mb-2">
          <KeyRound size={20} className="text-[#E60000]" />
          <h2 className="font-display text-2xl tracking-tighter text-white">{t("security.forceTitle")}</h2>
        </div>
        <p className="text-sm text-zinc-400 mb-5">{t("security.forceDesc")}</p>
        <ChangePasswordForm />
      </div>
    </div>
  );
}
